/**
 * Unit kit → unit modifiers.
 *
 * Every unit carries a `UnitKit` with three sources of modifiers:
 *   - launcher   — the weapon mount, tiered: preview quality, range (fuel),
 *                  yield (damage), blast radius, probe range;
 *   - suit       — tiered: armor, medkit, padding (jump landing), thrusters
 *                  (jump power), servos (movement);
 *   - equipment  — a few slotted items, each a bundle of modifiers;
 *   - traits     — the unit's own growth (fitness, jump): earned, not bought.
 *
 * `resolveUnitModifiers(kit, base)` folds them once, at match start, into one
 * `UnitModifiers` record on `player.modifiers`. Game code reads the record and
 * never the sources, so a geared bot is just a bot and a quick-match kit is
 * just a kit. With an empty kit the record equals today's constants exactly:
 * the system changes nothing until something is equipped.
 *
 * Also home to the aim-preview window (how much of the true flight is shown
 * for a preview quality, and the dial-in rule).
 */

import type {
  Vec3, GearUpgradeState, UnitModifiers, UnitKit, UnitTraits, LauncherModKey, SuitModKey, EquipmentKey, EquipmentDef,
} from '../types';
import { GEAR_UPGRADES, MAX_PROJECTILE_TIME, BASE_MOVEMENT_RANGE, FITNESS_MOVEMENT_BONUS, JUMP_HEIGHT_BONUS } from '../constants';

// =============================================================================
// Defaults — today's game, exactly
// =============================================================================

export const DEFAULT_UNIT_MODIFIERS: UnitModifiers = {
  previewQuality: 1,
  probeRangeMult: 1,
  healMult: 1,
  armorReduction: 0,
  fuelMult: 1,
  damageMult: 1,
  blastMult: 1,
  movementRange: BASE_MOVEMENT_RANGE,
  jumpVelocityBonus: 0,
  jumpFallDamageMult: 1,
  stealthMult: 1,
  sensorMult: 1,
};

export function defaultUnitKit(): UnitKit {
  return { launcher: {}, suit: {}, equipment: [], traits: { fitness: 0, jump: 0 } };
}

// =============================================================================
// Catalogue
// =============================================================================

/** Launcher mod → the gear entry whose tiers hold its values (campaign store). */
export const LAUNCHER_GEAR: Record<LauncherModKey, string> = {
  preview: 'preview', range: 'allFuel', yield: 'allDamage', blast: 'allBlastRadius', probe: 'probeRange',
};
/** Suit mods backed by a gear entry. The rest use SUIT_MOD_VALUES. */
export const SUIT_GEAR: Partial<Record<SuitModKey, string>> = { armor: 'armor', medkit: 'healBoost' };
/** Suit mods without a store entry yet (nothing can equip them; values are the design). Index = tier − 1. */
export const SUIT_MOD_VALUES: Record<'padding' | 'thrusters' | 'servos' | 'camo' | 'sensors', number[]> = {
  padding: [0.75, 0.5, 0.25],   // × landing damage from the unit's own jumps
  thrusters: [5, 10, 15],       // + jump velocity
  servos: [1, 2, 3],            // + movement points
  camo: [0.8, 0.65, 0.5],       // × the distance guards perceive this unit at (Phase 57.9)
  sensors: [1.25, 1.5, 1.8],    // × this unit's perception range as a guard (Phase 57.9)
};

export const EQUIPMENT_SLOTS = 2;
/** Slotted equipment. Defined so the kit has a shape; nothing acquires these yet. */
export const EQUIPMENT: Record<EquipmentKey, EquipmentDef> = {
  shockAbsorbers: {
    key: 'shockAbsorbers', name: 'Shock Absorbers', description: 'Halves landing damage from your own jumps.',
    modifiers: { jumpFallDamageMult: 0.5 },
  },
  ballastHarness: {
    key: 'ballastHarness', name: 'Ballast Harness', description: '−2 damage from every blast; −0.5 movement.',
    modifiers: { armorReduction: 2, movementRange: -0.5 },
  },
  ghillieCloak: {
    key: 'ghillieCloak', name: 'Ghillie Cloak', description: 'Guards see you at 60 % of the distance; −0.5 movement.',
    modifiers: { stealthMult: 0.6, movementRange: -0.5 },
  },
  sensorPack: {
    key: 'sensorPack', name: 'Sensor Pack', description: 'As a guard, perceive at 1.5× the distance.',
    modifiers: { sensorMult: 1.5 },
  },
};

/**
 * Named kits over the same fields (Phase 48). `standard` is the empty kit —
 * today's game. The others are the harness's measuring sticks and the proposal
 * for the quick-play Kit row (BUILDLIST 37.12 "Standard / Scout / Assault /
 * Entrenched …"): every tier here already exists in the model, so a preset is
 * only data. Nothing in play selects them yet.
 */
export type KitPresetKey = 'standard' | 'scout' | 'assault' | 'entrenched' | 'mobile' | 'infiltrator' | 'sentinel';
export const KIT_PRESETS: Record<KitPresetKey, UnitKit> = {
  standard: { launcher: {}, suit: {}, equipment: [], traits: { fitness: 0, jump: 0 } },
  // Sees more, walks more: probe range 3, ballistic computer 2, servos 2, one fitness level
  scout: { launcher: { probe: 3, preview: 2 }, suit: { servos: 2 }, equipment: [], traits: { fitness: 1, jump: 0 } },
  // Hits harder: yield 2 (+20% damage), blast 2 (+20% radius), range 1, light armor
  assault: { launcher: { yield: 2, blast: 2, range: 1 }, suit: { armor: 1 }, equipment: [], traits: { fitness: 0, jump: 0 } },
  // Takes hits: armor 3 (−10 per hit), medkit 2, range 2, ballast harness (−2 more, −0.5 movement)
  entrenched: { launcher: { range: 2 }, suit: { armor: 3, medkit: 2 }, equipment: ['ballastHarness'], traits: { fitness: 0, jump: 0 } },
  // Flies: thrusters 3 (+15 jump velocity), servos 3, padding 3, shock absorbers, two jump levels
  mobile: { launcher: {}, suit: { thrusters: 3, servos: 3, padding: 3 }, equipment: ['shockAbsorbers'], traits: { fitness: 0, jump: 2 } },
  // Phase 57.9 — the stealth pair. Infiltrator: seen at half the distance (camo 2 × cloak), soft landings, a little faster
  infiltrator: { launcher: {}, suit: { camo: 2, padding: 2, servos: 1 }, equipment: ['ghillieCloak'], traits: { fitness: 0, jump: 0 } },
  // Sentinel: a guard that sees 1.5 × 1.5 as far, lightly armoured
  sentinel: { launcher: {}, suit: { sensors: 2, armor: 1 }, equipment: ['sensorPack'], traits: { fitness: 0, jump: 0 } },
};
/** A fresh copy of a preset (kits are mutated per unit by callers). */
/** Player-facing names and one-line blurbs for the kit presets (the setup screen's kit picker). */
export const KIT_PRESET_INFO: Record<KitPresetKey, { name: string; blurb: string }> = {
  standard:    { name: 'Standard',    blurb: 'Today\'s unit. No modifications.' },
  scout:       { name: 'Scout',       blurb: 'Sees more, walks more: longer probes, a better preview, servos, one fitness level.' },
  assault:     { name: 'Assault',     blurb: 'Hits harder: yield and blast up, armour; a little slower.' },
  entrenched:  { name: 'Entrenched',  blurb: 'Takes less: heavy armour and a medkit; slow.' },
  mobile:      { name: 'Mobile',      blurb: 'Flies: thrusters, servos and padding, shock absorbers, two jump levels.' },
  infiltrator: { name: 'Infiltrator', blurb: 'Seen at 40 % of the distance (camo + cloak), soft landings, a little faster. The stealth pick.' },
  sentinel:    { name: 'Sentinel',    blurb: 'Sees more than twice as far as a guard (sensors + pack), light armour. The garrison pick.' },
};

/**
 * A kit with tiers laid over it: every non-zero tier replaces the base's (the setup screen's dev grid on top of
 * a chosen preset). Zero tiers leave the base alone; equipment is untouched.
 */
export function applyKitTiers(
  base: UnitKit,
  tiers: { launcher?: Partial<Record<LauncherModKey, number>>; suit?: Partial<Record<SuitModKey, number>>; traits?: Partial<Record<'fitness' | 'jump', number>> },
): UnitKit {
  const kit: UnitKit = { launcher: { ...base.launcher }, suit: { ...base.suit }, equipment: [...base.equipment], traits: { ...base.traits } };
  for (const [k, v] of Object.entries(tiers.launcher ?? {})) if (v && v > 0) kit.launcher[k as LauncherModKey] = v;
  for (const [k, v] of Object.entries(tiers.suit ?? {})) if (v && v > 0) kit.suit[k as SuitModKey] = v;
  for (const [k, v] of Object.entries(tiers.traits ?? {})) if (v && v > 0) kit.traits[k as 'fitness' | 'jump'] = v;
  return kit;
}

export function kitPreset(key: KitPresetKey): UnitKit {
  const k = KIT_PRESETS[key];
  return { launcher: { ...k.launcher }, suit: { ...k.suit }, equipment: [...k.equipment], traits: { ...k.traits } };
}

export const PREVIEW_QUALITY_LEVELS: Record<'full' | 'standard' | 'minimal', number> = { full: 1, standard: 0.5, minimal: 0.2 };
export const CAMPAIGN_BASE_PREVIEW_QUALITY = 0.35;

// =============================================================================
// Kit construction
// =============================================================================

function tierValue(gearKey: string | undefined, tier: number | undefined): number | undefined {
  if (!gearKey || !tier || tier <= 0) return undefined;
  const def = GEAR_UPGRADES[gearKey];
  if (!def) return undefined;
  return def.tiers[Math.min(tier, def.tiers.length) - 1]?.value;
}

function gearTier(gear: GearUpgradeState, gearKey: string): number {
  const def = GEAR_UPGRADES[gearKey];
  if (!def) return 0;
  const map = def.category === 'shared' ? gear.sharedTiers : def.category === 'item' ? gear.itemTiers : gear.weaponTiers;
  return (map as Record<string, number | undefined>)[gearKey] ?? 0;
}

/** A unit's kit from the campaign: team gear → launcher and suit tiers; the unit's own fitness / jump → traits. */
export function kitFromCampaign(gear: GearUpgradeState | undefined, traits?: Partial<UnitTraits>): UnitKit {
  const kit = defaultUnitKit();
  kit.traits = { fitness: traits?.fitness ?? 0, jump: traits?.jump ?? 0 };
  if (gear) {
    for (const [mod, gearKey] of Object.entries(LAUNCHER_GEAR) as Array<[LauncherModKey, string]>) {
      const t = gearTier(gear, gearKey);
      if (t > 0) kit.launcher[mod] = t;
    }
    for (const [mod, gearKey] of Object.entries(SUIT_GEAR) as Array<[SuitModKey, string]>) {
      const t = gearTier(gear, gearKey!);
      if (t > 0) kit.suit[mod] = t;
    }
  }
  return kit;
}

// =============================================================================
// Resolution
// =============================================================================

const MULT_FIELDS: Array<keyof UnitModifiers> = ['probeRangeMult', 'healMult', 'fuelMult', 'damageMult', 'blastMult', 'jumpFallDamageMult', 'stealthMult', 'sensorMult'];
const ADD_FIELDS: Array<keyof UnitModifiers> = ['armorReduction', 'movementRange', 'jumpVelocityBonus'];

/**
 * Fold a kit into one modifier record. `base` is the match's contribution
 * (today: preview quality). Launcher tiers set their field; suit tiers and
 * equipment stack (multiplicative for rates, additive for amounts); traits add
 * movement and jump velocity. Pure.
 */
export function resolveUnitModifiers(kit?: UnitKit, base: Partial<UnitModifiers> = {}): UnitModifiers {
  const m: UnitModifiers = { ...DEFAULT_UNIT_MODIFIERS, ...base };
  if (!kit) return m;

  // Launcher
  const preview = tierValue(LAUNCHER_GEAR.preview, kit.launcher.preview);
  if (preview !== undefined) m.previewQuality = Math.max(m.previewQuality, preview);
  const range = tierValue(LAUNCHER_GEAR.range, kit.launcher.range);
  if (range !== undefined) m.fuelMult *= range;
  const yld = tierValue(LAUNCHER_GEAR.yield, kit.launcher.yield);
  if (yld !== undefined) m.damageMult *= yld;
  const blast = tierValue(LAUNCHER_GEAR.blast, kit.launcher.blast);
  if (blast !== undefined) m.blastMult *= blast;
  const probe = tierValue(LAUNCHER_GEAR.probe, kit.launcher.probe);
  if (probe !== undefined) m.probeRangeMult *= probe;

  // Suit
  const armor = tierValue(SUIT_GEAR.armor, kit.suit.armor);
  if (armor !== undefined) m.armorReduction += armor;
  const medkit = tierValue(SUIT_GEAR.medkit, kit.suit.medkit);
  if (medkit !== undefined) m.healMult *= medkit;
  const pad = kit.suit.padding ? SUIT_MOD_VALUES.padding[Math.min(kit.suit.padding, 3) - 1] : undefined;
  if (pad !== undefined) m.jumpFallDamageMult *= pad;
  const thr = kit.suit.thrusters ? SUIT_MOD_VALUES.thrusters[Math.min(kit.suit.thrusters, 3) - 1] : undefined;
  if (thr !== undefined) m.jumpVelocityBonus += thr;
  const srv = kit.suit.servos ? SUIT_MOD_VALUES.servos[Math.min(kit.suit.servos, 3) - 1] : undefined;
  if (srv !== undefined) m.movementRange += srv;
  const camo = kit.suit.camo ? SUIT_MOD_VALUES.camo[Math.min(kit.suit.camo, 3) - 1] : undefined;
  if (camo !== undefined) m.stealthMult *= camo;
  const sen = kit.suit.sensors ? SUIT_MOD_VALUES.sensors[Math.min(kit.suit.sensors, 3) - 1] : undefined;
  if (sen !== undefined) m.sensorMult *= sen;

  // Equipment (slot-limited)
  for (const key of kit.equipment.slice(0, EQUIPMENT_SLOTS)) {
    const def = EQUIPMENT[key];
    if (!def) continue;
    for (const f of MULT_FIELDS) if (def.modifiers[f] !== undefined) (m[f] as number) *= def.modifiers[f] as number;
    for (const f of ADD_FIELDS) if (def.modifiers[f] !== undefined) (m[f] as number) += def.modifiers[f] as number;
    if (def.modifiers.previewQuality !== undefined) m.previewQuality = Math.max(m.previewQuality, def.modifiers.previewQuality);
  }

  // Traits
  m.movementRange += (kit.traits.fitness ?? 0) * FITNESS_MOVEMENT_BONUS;
  m.jumpVelocityBonus += (kit.traits.jump ?? 0) * JUMP_HEIGHT_BONUS;

  m.movementRange = Math.max(0.5, m.movementRange);
  return m;
}

// =============================================================================
// Preview window
// =============================================================================

export const PREVIEW_MIN_SECONDS = 3;
export const PREVIEW_MAX_STRIDE = 8;
export const PREVIEW_DIAL_IN_RADIUS = 4;

export interface PreviewWindow {
  seconds: number;
  stride: number;
  dialedIn: boolean;
}

export function previewSecondsForQuality(quality: number): number {
  const q = Math.max(0, Math.min(1, quality));
  return PREVIEW_MIN_SECONDS + (MAX_PROJECTILE_TIME - PREVIEW_MIN_SECONDS) * q * q;
}

export function previewStrideForQuality(quality: number): number {
  const q = Math.max(0, Math.min(1, quality));
  return Math.max(1, Math.round(1 + (PREVIEW_MAX_STRIDE - 1) * (1 - q)));
}

export function previewWindow(quality: number, fullPoints: Vec3[], pastImpacts: Vec3[], physDt: number): PreviewWindow {
  const flightSeconds = Math.max(0, fullPoints.length - 1) * physDt;
  const q = Math.max(0, Math.min(1, quality));
  if (q >= 1) return { seconds: flightSeconds, stride: 1, dialedIn: false };

  const r2 = PREVIEW_DIAL_IN_RADIUS * PREVIEW_DIAL_IN_RADIUS;
  let dialedIn = false;
  if (pastImpacts.length > 0) {
    outer: for (const p of fullPoints) {
      for (const h of pastImpacts) {
        const dx = p.x - h.x, dy = p.y - h.y, dz = p.z - h.z;
        if (dx * dx + dy * dy + dz * dz <= r2) { dialedIn = true; break outer; }
      }
    }
  }
  if (dialedIn) return { seconds: flightSeconds, stride: 1, dialedIn: true };
  return {
    seconds: Math.min(flightSeconds, previewSecondsForQuality(q)),
    stride: previewStrideForQuality(q),
    dialedIn: false,
  };
}

export function previewPoints(fullPoints: Vec3[], window: PreviewWindow, physDt: number): Vec3[] {
  if (fullPoints.length === 0) return [];
  const lastIdx = Math.min(fullPoints.length - 1, Math.floor(window.seconds / physDt));
  const out: Vec3[] = [];
  for (let i = 0; i <= lastIdx; i += window.stride) out.push(fullPoints[i]);
  if (out[out.length - 1] !== fullPoints[lastIdx]) out.push(fullPoints[lastIdx]);
  return out;
}
